import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";
import { useApolloClient, useLazyQuery } from "@apollo/client";

import { Table, Button, FilterValues, PageHeader, Card, Filter, FilterList } from "@cofano/cck";

import { Box, CircularProgress } from "@mui/material";
import Edit from "@mui/icons-material/Edit";

import { TableId, useBackendTable } from "components/table";
import Helmet from "components/helmet/Helmet";
import CommentsWidget from "components/comments/CommentsWidget";
import useComments from "components/comments/useComments";
import CommentIndicatorButton from "components/comments/CommentIndicatorButton";

import StatusChanger from "view/statuses/components/changer/StatusChanger";
import BulkStatusChanger from "view/statuses/components/changer/BulkStatusChanger";
import CargosDownload from "view/cargos/components/download/CargosDownload";
import useCargoTable from "view/cargos/hooks/useCargoTable";

import { Language, DataFormat, usePermission, Fields, hasPluginPermission, hasOptionalFieldPermission } from "imports/Utils";

import { LocationLink, RelationLink, ShipLink, VoyageLink } from "utils/LinkUtils";
import { FormattedDate } from "utils/DateUtils";

import ContainerBulkEditDialog from "./components/dialogs/ContainerBulkEditDialog";

import { ChildPropsFromArray, ChildPropsFromPagination } from "types/commons";
import { CommentEntityType, SortDirection, StatusType } from "__generated__/enums";
import { GetContainersDocument, GetContainersQuery } from "__generated__/view/containers/graphql/queries.generated";
import { GetAllAdnDocument, GetAllAdnQuery } from "__generated__/view/cargos/graphql/cargo.queries.generated";

type ContainerFromQuery = ChildPropsFromPagination<GetContainersQuery, "cargos">;
type UnNumberFromQuery = ChildPropsFromArray<GetAllAdnQuery, "allAdn">;

const Containers = ({ ...otherProps }) => {

	const session = useStoreState(state => state.session);

	const [ getAllAdn ] = useLazyQuery(GetAllAdnDocument);
	const [ unNumbers, setUnNumbers ] = useState<UnNumberFromQuery[] | null>(null);

	useEffect(() => {
		if (hasOptionalFieldPermission(session, Fields.ORDER, "unNumbers")) {
			getAllAdn()
				.then((response) => {
					setUnNumbers(response.data?.allAdn ?? []);
				});
		} else {
			setUnNumbers([]);
		}
	}, []);

	return (
		<React.Fragment>
			{unNumbers ? (
				<Page
					unNumbers={unNumbers}
					{...otherProps} />
			) : (
				<CircularProgress size={30} />
			)}
		</React.Fragment>
	);
};

const Page = ({
	unNumbers,
	fixedFilters = [],
	disableHeader = false,
}: {
	unNumbers: UnNumberFromQuery[],
	fixedFilters?: FilterList;
	disableHeader?: boolean;
}) => {

	const session = useStoreState(state => state.session);

	const client = useApolloClient();
	const navigate = useNavigate();

	const [ bulkEditDialog, setBulkEditDialog ] = useState<{ open: boolean; selection: ContainerFromQuery[] }>({
		open: false,
		selection: [],
	});

	const { isAdmin, isCompanyUser, isOperator, isTerminal, isMainUserLimited } = usePermission();

	const { getSearchInput, filterFields } = useCargoTable();

	const { tableProps, filterProps, filterValuesProps, refetch, filters, clearSelection } = useBackendTable({
		defaultFilter: fixedFilters,
		id: TableId.Containers,
		selectSettings: {
			selectionEnabled: true,
			isFullSelectEnabled: true,
		},
		onChange: async(pagination, sort, filter, callback) => {
			try {
				const result = await client.query({
					query: GetContainersDocument,
					variables: {
						pagination: {
							...pagination,
							sortList: sort ?? [ {
								field: "created",
								direction: SortDirection.Desc,
							} ],
						},
						search: getSearchInput(filter),
					},
				});

				callback({
					content: result.data.cargos.content,
					totalResults: result.data.cargos.totalElements,
				});
			} catch {
				callback({
					content: [],
					totalResults: 0,
				});
			}
		},
	});

	const { commentsProps, openComment } = useComments({
		cargos: tableProps.content as ContainerFromQuery[],
		onChange: () => refetch(),
	});

	const excludeFixedFilters = (item: { id: string }) => {
		return fixedFilters.find(it => it.property === item.id) === undefined;
	};

	const fieldEnabled = (field: string) => {
		return ((isAdmin || isCompanyUser || isOperator || isTerminal || isMainUserLimited) && hasOptionalFieldPermission(session, Fields.ORDER, field));
	};

	return (
		<div>
			{!disableHeader ? (
				<PageHeader>
					<PageHeader.Title>
						{Language("CONTAINERS")}
						<Helmet title={intl.formatMessage({ id: "CONTAINERS" })} />
					</PageHeader.Title>
				</PageHeader>
			) : <React.Fragment />}

			{filterValuesProps.values.length - (fixedFilters.length || 0) > 0 && (
				<Box mb={2}>
					<Card disablePadding>
						<FilterValues
							{...filterValuesProps}
							fields={Object.values(filterFields).map(it => ({
								id: it.id,
								title: it.title,
								renderFilterValue: it.renderFilterValue,
							})).filter(excludeFixedFilters)} />
					</Card>
				</Box>
			)}

			<Card disablePadding>
				<Table
					{...tableProps}
					onRowDoubleClick={(row, event) => {
						const url = `/view/cargos/${row.id}/details`;

						if (event.ctrlKey) {
							window.open(url);
						} else {
							navigate(url);
						}
					}}
					filterComponent={() => (
						<Filter
							{...filterProps}
							fields={[
								filterFields.Relations,
								filterFields.CharterNumber,
								filterFields.CustomerReference,
								filterFields.Ships,
								filterFields.Voyages,
								filterFields.LoadingLocations,
								filterFields.DischargingLocations,
								filterFields.CurrentStatuses,
								filterFields.ContainerNumber,
								filterFields.ContainerType,
								filterFields.Empty,
								filterFields.LoadingReference,
								filterFields.DischargingReference,
								filterFields.ConnectingVisitLoadingCallReferenceNumber,
								filterFields.ConnectingVisitDischargingCallReferenceNumber,
								filterFields.HasDangerousGoods,
							].filter(excludeFixedFilters)} />
					)}
					columns={[ {
						id: "relation",
						title: intl.formatMessage({ id: "CLIENT" }),
						render: (row: ContainerFromQuery) => <RelationLink relation={row.charter?.debtor} />,
						sortable: true,
						sortKey: "charter.debtor.name",
						width: 150,
					}, {
						id: "charterNumber",
						title: intl.formatMessage({ id: "CHARTER_ORDER_NUMBER" }),
						render: (row: ContainerFromQuery) => row.charter?.charterNumber,
						sortable: true,
						sortKey: "charter.charterNumber",
						width: 150,
					}, {
						id: "customerReference",
						title: intl.formatMessage({ id: "CUSTOMER_REFERENCE" }),
						render: (row: ContainerFromQuery) => {
							if (row.charter && "customerReference" in row.charter) {
								return row.charter.customerReference;
							} else {
								return undefined;
							}
						},
						sortable: true,
						sortKey: "charter.customerReference",
						width: 150,
					}, {
						id: "ships",
						title: intl.formatMessage({ id: "SHIP" }),
						render: (row: ContainerFromQuery) => <ShipLink ship={row.firstLoadingAction?.call?.voyage?.ship} />,
						sortable: true,
						sortKey: "firstLoadingAction.call.voyage.ship.name",
					}, {
						id: "voyageNumber",
						title: intl.formatMessage({ id: "VOYAGE_NUMBER" }),
						render: (row: ContainerFromQuery) => <VoyageLink voyage={row.firstLoadingAction?.call?.voyage} />,
						sortable: true,
						sortKey: "firstLoadingAction.call.voyage.voyageNumber",
						width: 150,
					}, {
						id: "loadingLocations",
						title: intl.formatMessage({ id: "ORDER_LOADING_PLACE" }),
						render: (row: ContainerFromQuery) => <LocationLink location={row.firstLoadingAction?.location} />,
						sortable: true,
						sortKey: "firstLoadingAction.location.description",
						width: 150,
					}, {
						id: "dischargingLocations",
						title: intl.formatMessage({ id: "ORDER_DISCHARGING_PLACE" }),
						render: (row: ContainerFromQuery) => <LocationLink location={row.lastDischargingAction?.location} />,
						sortable: true,
						sortKey: "lastDischargingAction.location.description",
						width: 150,
					}, {
						id: "currentStatuses",
						title: intl.formatMessage({ id: "STATUS" }),
						render: (row: ContainerFromQuery) => (
							<StatusChanger
								session={session}
								entity={row}
								statusType={StatusType.Cargo}
								refetch={refetch} />
						),
						sortable: true,
						sortKey: "lastStatusChange.status.description",
						disabled: !hasPluginPermission(session, "cargoStatuses"),
						width: 175,
					}, {
						id: "containerNumber",
						title: intl.formatMessage({ id: "CONTAINER_NUMBER" }),
						sortable: true,
						width: 150,
					}, {
						id: "containerType",
						title: intl.formatMessage({ id: "CARGO_CONTAINER_TYPE" }),
						render: (row: ContainerFromQuery) => DataFormat.containerType(row.containerType),
						sortable: true,
						sortKey: "containerType.isoTypeCode",
						width: 150,
					}, {
						id: "isEmpty",
						title: intl.formatMessage({ id: "CARGO_FULL_OR_EMPTY" }),
						render: (row: ContainerFromQuery) => row.empty ? intl.formatMessage({ id: "CARGO_EMPTY" }) : intl.formatMessage({ id: "CARGO_FULL" }),
						sortable: true,
						sortKey: "empty",
					}, {
						id: "loadingReference",
						title: intl.formatMessage({ id: "LOADING_REFERENCE" }),
						sortable: true,
						width: 150,
					}, {
						id: "dischargingReference",
						title: intl.formatMessage({ id: "DISCHARGING_REFERENCE" }),
						sortable: true,
						width: 150,
					}, {
						id: "connectingVisitLoadingCallReferenceNumber",
						title: intl.formatMessage({ id: "CONNECTING_VISIT_LOADING_CALL_REFERENCE" }),
						width: 150,
					}, {
						id: "connectingVisitDischargingCallReferenceNumber",
						title: intl.formatMessage({ id: "CONNECTING_VISIT_DISCHARGING_CALL_REFERENCE" }),
						width: 150,
					}, {
						id: "bookingReference",
						title: intl.formatMessage({ id: "CARGO_BOOKING_REFERENCE" }),
						sortable: true,
						width: 200,
						defaultVisible: false,
						disabled: !fieldEnabled("bookingReference"),
					}, {
						id: "bestLoadingMooringPosition",
						title: intl.formatMessage({ id: "BEST_LOADING_MOORING_POSITION" }),
						render: (row: ContainerFromQuery) => {
							const bolder = row.bestLoadingMooringPosition;
							return bolder ? `${bolder.quay?.name1} - ${bolder.sequenceNumber}` : "";
						},
						width: 200,
						defaultVisible: false,
						disabled: !fieldEnabled("bestMooringPosition"),
					}, {
						id: "bestDischargingMooringPosition",
						title: intl.formatMessage({ id: "BEST_DISCHARGING_MOORING_POSITION" }),
						render: (row: ContainerFromQuery) => {
							const bolder = row.bestDischargingMooringPosition;
							return bolder ? `${bolder.quay?.name1} - ${bolder.sequenceNumber}` : "";
						},
						width: 200,
						defaultVisible: false,
						disabled: !fieldEnabled("bestMooringPosition"),
					}, {
						id: "loaded",
						title: intl.formatMessage({ id: "CARGO_LOADED" }),
						sortable: true,
						defaultVisible: false,
						render: (row: ContainerFromQuery) => <FormattedDate date={row.loaded} withoutYear />,
					}, {
						id: "canSkipperEditLoadedTimestamp",
						title: intl.formatMessage({ id: "CAN_SKIPPER_EDIT_LOADED_TIMESTAMP" }),
						defaultVisible: false,
						render: (row: ContainerFromQuery) => DataFormat.boolean(row.canSkipperEditLoadedTimestamp),
					}, {
						id: "discharged",
						title: intl.formatMessage({ id: "CARGO_DISCHARGED" }),
						sortable: true,
						defaultVisible: false,
						render: (row: ContainerFromQuery) => <FormattedDate date={row.discharged} withoutYear />,
					}, {
						id: "canSkipperEditDischargedTimestamp",
						title: intl.formatMessage({ id: "CAN_SKIPPER_EDIT_DISCHARGED_TIMESTAMP" }),
						defaultVisible: false,
						render: (row: ContainerFromQuery) => DataFormat.boolean(row.canSkipperEditDischargedTimestamp),
					}, {
						id: "transportType",
						title: intl.formatMessage({ id: "CARGO_TRANSPORT_TYPE" }),
						sortable: true,
						width: 150,
						defaultVisible: false,
						disabled: !fieldEnabled("transportType"),
					}, {
						id: "productDetails",
						title: intl.formatMessage({ id: "PRODUCT_DETAILS" }),
						sortable: true,
						width: 200,
						defaultVisible: false,
						disabled: !fieldEnabled("productDetails"),
					}, {
						id: "unNumbers",
						title: intl.formatMessage({ id: "CARGO_UN_NUMBER" }),
						render: (row: ContainerFromQuery) => {
							return row.unNumbers
								?.map(item => unNumbers.find(u => u.identifier === item?.unNumberId))
								.map(unNumber => unNumber?.identifier || intl.formatMessage({ id: "UNKNOWN" }))
								.join(", ");
						},
						width: 200,
						defaultVisible: false,
						disabled: !fieldEnabled("unNumbers"),
					}, {
						id: "hasDangerousGoods",
						title: intl.formatMessage({ id: "CARGO_DANGEROUS_GOOD" }),
						render: (row: ContainerFromQuery) => DataFormat.boolean(row.hasDangerousGoods),
						defaultVisible: false,
					}, {
						id: "reeferCheckType",
						title: intl.formatMessage({ id: "REEFER_CHECK_TYPE" }),
						sortable: true,
						defaultVisible: false,
						disabled: !fieldEnabled("reeferCheckType"),
					}, {
						id: "weight",
						title: intl.formatMessage({ id: "CARGO_WEIGHT" }),
						render: (row: ContainerFromQuery) => DataFormat.tonnage(row.weight),
						width: 150,
						defaultVisible: false,
						disabled: !fieldEnabled("weight"),
					}, {
						id: "tareWeight",
						title: intl.formatMessage({ id: "CARGO_TARE_WEIGHT" }),
						render: (row: ContainerFromQuery) => DataFormat.tonnage(row.tareWeight),
						sortable: true,
						defaultVisible: false,
						disabled: !fieldEnabled("tareWeight"),
					}, {
						id: "grossWeight",
						title: intl.formatMessage({ id: "CARGO_GROSS_WEIGHT" }),
						render: (row: ContainerFromQuery) => DataFormat.tonnage(row.grossWeight),
						sortable: true,
						width: 150,
						defaultVisible: false,
						disabled: !fieldEnabled("grossWeight"),
					}, {
						id: "verifiedGrossWeight",
						title: intl.formatMessage({ id: "CARGO_VERIFIED_GROSS_WEIGHT" }),
						render: (row: ContainerFromQuery) => DataFormat.tonnage(row.verifiedGrossWeight),
						sortable: true,
						width: 200,
						defaultVisible: false,
						disabled: !fieldEnabled("verifiedGrossWeight"),
					}, {
						id: "imoClass",
						title: intl.formatMessage({ id: "CARGO_IMO_CLASS" }),
						sortable: true,
						width: 150,
						defaultVisible: false,
						disabled: !fieldEnabled("imoClass"),
					}, {
						id: "billOfLadingNumber",
						title: intl.formatMessage({ id: "BL_NUMBER" }),
						sortable: true,
						defaultVisible: false,
						disabled: !fieldEnabled("billOfLading"),
					}, {
						id: "desiredTemperature",
						title: intl.formatMessage({ id: "CARGO_DESIRED_TEMPERATURE" }),
						render: (row: ContainerFromQuery) => DataFormat.temperature(row.desiredTemperature),
						sortable: true,
						defaultVisible: false,
						disabled: !fieldEnabled("desiredTemperature"),
					}, {
						id: "temperatureRangeLower",
						title: intl.formatMessage({ id: "CARGO_TEMPERATURE_RANGE_LOWER" }),
						render: (row: ContainerFromQuery) => DataFormat.temperature(row.temperatureRangeLower),
						sortable: true,
						width: 250,
						defaultVisible: false,
						disabled: !fieldEnabled("temperatureRange"),
					}, {
						id: "temperatureRangeUpper",
						title: intl.formatMessage({ id: "CARGO_TEMPERATURE_RANGE_UPPER" }),
						render: (row: ContainerFromQuery) => DataFormat.temperature(row.temperatureRangeUpper),
						sortable: true,
						width: 250,
						defaultVisible: false,
						disabled: !fieldEnabled("temperatureRange"),
					}, {
						id: "humidity",
						title: intl.formatMessage({ id: "CARGO_REEFER_HUMIDITY" }),
						render: (row: ContainerFromQuery) => DataFormat.percentage(row.humidity),
						sortable: true,
						width: 150,
						defaultVisible: false,
						disabled: !fieldEnabled("humidity"),
					}, {
						id: "sealed",
						title: intl.formatMessage({ id: "CARGO_ISSEALED" }),
						render: (row: ContainerFromQuery) => DataFormat.boolean(row.sealed),
						sortable: true,
						defaultVisible: false,
						disabled: !fieldEnabled("sealNumber"),
					}, {
						id: "sealNumber",
						title: intl.formatMessage({ id: "CARGO_SEALNUMBER" }),
						sortable: true,
						width: 150,
						defaultVisible: false,
						disabled: !fieldEnabled("sealNumber"),
					}, {
						id: "remarks",
						title: intl.formatMessage({ id: "CARGO_REMARKS" }),
						sortable: true,
						width: 150,
						defaultVisible: false,
						disabled: !fieldEnabled("remarks"),
					}, {
						id: "scacCode",
						title: intl.formatMessage({ id: "CARGO_CARRIER_SCAC_LOADING" }),
						sortable: true,
						width: 150,
						defaultVisible: false,
						disabled: !fieldEnabled("carrierScac"),
					}, {
						id: "scacCodeDischarging",
						title: intl.formatMessage({ id: "CARGO_CARRIER_SCAC_DISCHARGING" }),
						sortable: true,
						width: 150,
						defaultVisible: false,
						disabled: !fieldEnabled("carrierScacDischarging"),
					}, {
						id: "overheight",
						title: intl.formatMessage({ id: "CARGO_OOG_OVER_HEIGHT" }),
						render: (row: ContainerFromQuery) => DataFormat.simpleUnit(row.overheight, DataFormat.UNITS.CENTIMETER),
						sortable: true,
						defaultVisible: false,
						disabled: !fieldEnabled("outOfGauge"),
					}, {
						id: "overlength",
						title: intl.formatMessage({ id: "CARGO_OOG_OVER_LENGTH" }),
						render: (row: ContainerFromQuery) => DataFormat.simpleUnit(row.overlength, DataFormat.UNITS.CENTIMETER),
						sortable: true,
						width: 150,
						defaultVisible: false,
						disabled: !fieldEnabled("outOfGauge"),
					}, {
						id: "overlengthBehind",
						title: intl.formatMessage({ id: "CARGO_OOG_OVER_LENGTH_BEHIND" }),
						render: (row: ContainerFromQuery) => DataFormat.simpleUnit(row.overlengthBehind, DataFormat.UNITS.CENTIMETER),
						sortable: true,
						width: 150,
						defaultVisible: false,
						disabled: !fieldEnabled("outOfGauge"),
					}, {
						id: "overwidthRight",
						title: intl.formatMessage({ id: "CARGO_OOG_OVER_WIDTH_RIGHT" }),
						render: (row: ContainerFromQuery) => DataFormat.simpleUnit(row.overwidthRight, DataFormat.UNITS.CENTIMETER),
						sortable: true,
						width: 200,
						defaultVisible: false,
						disabled: !fieldEnabled("outOfGauge"),
					}, {
						id: "overwidthLeft",
						title: intl.formatMessage({ id: "CARGO_OOG_OVER_WIDTH_LEFT" }),
						render: (row: ContainerFromQuery) => DataFormat.simpleUnit(row.overwidthLeft, DataFormat.UNITS.CENTIMETER),
						sortable: true,
						width: 150,
						defaultVisible: false,
						disabled: !fieldEnabled("outOfGauge"),
					}, {
						id: "doorOpen",
						title: intl.formatMessage({ id: "CARGO_OOG_DOOR_CLOSED" }),
						render: (row: ContainerFromQuery) => DataFormat.boolean(row.doorOpen),
						sortable: true,
						defaultVisible: false,
						disabled: !fieldEnabled("outOfGauge"),
					}, {
						id: "chains",
						title: intl.formatMessage({ id: "CARGO_OOG_CHAINS" }),
						render: (row: ContainerFromQuery) => DataFormat.boolean(row.chains),
						sortable: true,
						defaultVisible: false,
						disabled: !fieldEnabled("outOfGauge"),
					}, {
						id: "generator",
						title: intl.formatMessage({ id: "CARGO_OOG_GENERATOR" }),
						render: (row: ContainerFromQuery) => DataFormat.boolean(row.generator),
						sortable: true,
						defaultVisible: false,
						disabled: !fieldEnabled("outOfGauge"),
					}, {
						id: "damage",
						title: intl.formatMessage({ id: "CARGO_DAMAGE" }),
						sortable: true,
						width: 150,
						defaultVisible: false,
						disabled: !fieldEnabled("damage"),
					}, {
						id: "releasedForBarge",
						title: intl.formatMessage({ id: "CARGO_RELEASED_FOR_BARGE" }),
						render: (row: ContainerFromQuery) => DataFormat.boolean(row.releasedForBarge),
						sortable: true,
						width: 200,
						defaultVisible: false,
						disabled: !fieldEnabled("releasedForModality"),
					}, {
						id: "releasedForTrain",
						title: intl.formatMessage({ id: "CARGO_RELEASED_FOR_TRAIN" }),
						render: (row: ContainerFromQuery) => DataFormat.boolean(row.releasedForTrain),
						sortable: true,
						width: 200,
						defaultVisible: false,
						disabled: !fieldEnabled("releasedForModality"),
					}, {
						id: "releasedForTruck",
						title: intl.formatMessage({ id: "CARGO_RELEASED_FOR_TRUCK" }),
						render: (row: ContainerFromQuery) => DataFormat.boolean(row.releasedForTruck),
						sortable: true,
						width: 200,
						defaultVisible: false,
						disabled: !fieldEnabled("releasedForModality"),
					}, {
						id: "masterBundleContainerNumber",
						title: intl.formatMessage({ id: "CARGO_MASTER_BUNDLE_CONTAINER_NUMBER" }),
						sortable: true,
						width: 200,
						defaultVisible: false,
						disabled: !fieldEnabled("masterBundleContainerNumber"),
					}, {
						id: "deepseaShipNameLoading",
						title: intl.formatMessage({ id: "CARGO_DEEPSEA_SHIP_NAME_LOADING" }),
						sortable: true,
						width: 300,
						defaultVisible: false,
						disabled: !fieldEnabled("deepsea"),
					}, {
						id: "deepseaShipImoLoading",
						title: intl.formatMessage({ id: "CARGO_DEEPSEA_SHIP_IMO_LOADING" }),
						sortable: true,
						width: 300,
						defaultVisible: false,
						disabled: !fieldEnabled("deepsea"),
					}, {
						id: "deepseaVoyageNumberLoading",
						title: intl.formatMessage({ id: "CARGO_DEEPSEA_VOYAGE_NUMBER_LOADING" }),
						sortable: true,
						width: 300,
						defaultVisible: false,
						disabled: !fieldEnabled("deepsea"),
					}, {
						id: "deepseaEtaLoading",
						title: intl.formatMessage({ id: "CARGO_DEEPSEA_ETA_LOADING" }),
						render: (row: ContainerFromQuery) => <FormattedDate date={row.deepseaEtaLoading} withoutYear />,
						sortable: true,
						width: 300,
						defaultVisible: false,
						disabled: !fieldEnabled("deepsea"),
					}, {
						id: "deepseaShipNameDischarging",
						title: intl.formatMessage({ id: "CARGO_DEEPSEA_SHIP_NAME_DISCHARGING" }),
						sortable: true,
						width: 300,
						defaultVisible: false,
						disabled: !fieldEnabled("deepsea"),
					}, {
						id: "deepseaShipImoDischarging",
						title: intl.formatMessage({ id: "CARGO_DEEPSEA_SHIP_IMO_DISCHARGING" }),
						sortable: true,
						width: 300,
						defaultVisible: false,
						disabled: !fieldEnabled("deepsea"),
					}, {
						id: "deepseaVoyageNumberDischarging",
						title: intl.formatMessage({ id: "CARGO_DEEPSEA_VOYAGE_NUMBER_DISCHARGING" }),
						sortable: true,
						width: 300,
						defaultVisible: false,
						disabled: !fieldEnabled("deepsea"),
					}, {
						id: "deepseaEtaDischarging",
						title: intl.formatMessage({ id: "CARGO_DEEPSEA_ETA_DISCHARGING" }),
						render: (row: ContainerFromQuery) => <FormattedDate date={row.deepseaEtaDischarging} withoutYear />,
						sortable: true,
						width: 300,
						defaultVisible: false,
						disabled: !fieldEnabled("deepsea"),
					}, {
						id: "deepseaEstimateDischarge",
						title: intl.formatMessage({ id: "CARGO_DEEPSEA_ESTIMATE_DISCHARGE" }),
						render: (row: ContainerFromQuery) => <FormattedDate date={row.deepseaEstimateDischarge} withoutYear />,
						sortable: true,
						width: 300,
						defaultVisible: false,
						disabled: !fieldEnabled("deepsea"),
					}, {
						id: "previousInlandTerminal",
						title: intl.formatMessage({ id: "CARGO_PREVIOUS_INLAND_TERMINAL" }),
						render: (row: ContainerFromQuery) => <LocationLink location={row.previousInlandTerminal} />,
						sortable: true,
						sortKey: "previousInlandTerminal.description",
						width: 200,
						defaultVisible: false,
						disabled: !fieldEnabled("voyageInfo"),
					}, {
						id: "nextInlandTerminal",
						title: intl.formatMessage({ id: "CARGO_NEXT_INLAND_TERMINAL" }),
						render: (row: ContainerFromQuery) => <LocationLink location={row.nextInlandTerminal} />,
						sortable: true,
						sortKey: "nextInlandTerminal.description",
						width: 200,
						defaultVisible: false,
						disabled: !fieldEnabled("voyageInfo"),
					}, {
						id: "previousPort",
						title: intl.formatMessage({ id: "CARGO_PREVIOUS_PORT" }),
						sortable: true,
						width: 200,
						defaultVisible: false,
						disabled: !fieldEnabled("voyageInfo"),
					}, {
						id: "nextPort",
						title: intl.formatMessage({ id: "CARGO_NEXT_PORT" }),
						sortable: true,
						width: 200,
						defaultVisible: false,
						disabled: !fieldEnabled("voyageInfo"),
					}, {
						id: "finalPort",
						title: intl.formatMessage({ id: "CARGO_FINAL_PORT" }),
						sortable: true,
						width: 200,
						defaultVisible: false,
						disabled: !fieldEnabled("voyageInfo"),
					}, {
						id: "revenue",
						title: intl.formatMessage({ id: "REVENUE" }),
						render: (row: ContainerFromQuery) => DataFormat.price(row.revenue),
						disabled: !fieldEnabled("revenue"),
						defaultVisible: true,
					}, {
						id: "created",
						title: intl.formatMessage({ id: "CREATED" }),
						sortable: true,
						render: (row: ContainerFromQuery) => <FormattedDate date={row.created} withoutYear />,
					} ].filter(excludeFixedFilters)}
					actions={[ {
						render: (selection: ContainerFromQuery[] | null, isFullSelect) => {
							const selectedCargos = selection ?? [];
							return (
								!isFullSelect && (
									<Button
										disabled={selectedCargos.length === 0 || selectedCargos.some(cargo => cargo.actions.length > 2)}
										onClick={() => {
											setBulkEditDialog({
												open: true,
												selection: selectedCargos,
											});
										}}
										size="small"
										variant="outlined">
										<Edit fontSize="small" />
									</Button>
								)
							);
						},
					}, {
						render: (selection, isFullSelect, event, state) =>
							<CargosDownload
								selection={selection || []}
								isFullSelect={isFullSelect}
								filters={filters}
								columns={state.columnSettings.columns}
								totalResults={tableProps.paginationSettings.totalResults}
								onDownload={() => clearSelection()} />,
					}, {
						render: (selection, isFullSelect) => {
							return (
								!isFullSelect && (
									<BulkStatusChanger
										statusType={StatusType.Cargo}
										entities={selection ?? []}
										refetch={refetch} />
								)
							);
						},
					} ]}
					rowActions={[ {
						icon: <React.Fragment />,
						onClick: () => {},
						render: (row: ContainerFromQuery) => (
							<CommentIndicatorButton
								entity={row}
								onClick={() => openComment(row.id, CommentEntityType.Cargo)}
								session={session} />
						),
					} ]} />
			</Card>

			<ContainerBulkEditDialog
				open={bulkEditDialog.open}
				onClose={() => {
					setBulkEditDialog({
						open: false,
						selection: [],
					});
				}}
				containerIds={bulkEditDialog.selection.map(it => it.id)}
				refetch={refetch}
			/>

			<CommentsWidget {...commentsProps} />
		</div>
	);
};

export default Containers;
